import { Lock } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button, EmptyState, ErrorState, Skeleton } from '@athlete/ui';
import { friendlyMessage, isApiError } from '@/api';

export function NoAccess({ title = 'You do not have access to this section', description }: { title?: string; description?: ReactNode }) {
  return (
    <div className="rounded-lg border bg-surface">
      <EmptyState
        icon={Lock}
        title={title}
        description={description ?? 'Your role does not include this area. Ask an administrator if you think you should have access.'}
      />
    </div>
  );
}

/** What QueryBoundary reads from a query: a plain query and a paged (infinite) one both have it. */
interface Loadable<T> {
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  error: unknown;
  data: T | undefined;
  refetch: () => unknown;
}

/**
 * Standard loading / error / permission handling for any query, so every screen behaves the same:
 * skeleton while loading (never a full-screen spinner), plain-language error with Retry,
 * and a permission message for 403s.
 */
export function QueryBoundary<T>({
  query,
  skeleton,
  children,
}: {
  query: Loadable<T>;
  skeleton?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <>{skeleton ?? <Skeleton className="h-48 w-full" />}</>;
  if (query.isError) {
    if (isApiError(query.error) && query.error.status === 403) return <NoAccess />;
    return (
      <div className="rounded-lg border bg-surface">
        <ErrorState
          title="We could not load this"
          reason={friendlyMessage(query.error)}
          requestId={isApiError(query.error) ? query.error.requestId : undefined}
          actions={
            <Button onClick={() => query.refetch()} loading={query.isFetching}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }
  return <>{children(query.data as T)}</>;
}
